import { Box, Text } from '@mantine/core';

export type RouteMapPoint = {
    lat: number;
    lng: number;
    label?: string;
};

type Props = {
    points: RouteMapPoint[];
    height?: number;
};

/** Equirectangular SVG preview — enough to see stop order and spacing. */
export const RouteMap = ({ points, height = 240 }: Props) => {
    if (points.length < 1) {
        return (
            <Box
                h={height}
                style={{
                    borderRadius: 'var(--mantine-radius-md)',
                    border: '1px dashed var(--mantine-color-gray-3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'var(--mantine-color-gray-0)',
                }}
            >
                <Text size='sm' c='dimmed'>
                    Select attractions to preview the route
                </Text>
            </Box>
        );
    }

    const lats = points.map((p) => p.lat);
    const lngs = points.map((p) => p.lng);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);

    const pad = 0.002;
    const latSpan = Math.max(maxLat - minLat, 0.004) + pad * 2;
    const lngSpan = Math.max(maxLng - minLng, 0.004) + pad * 2;
    const width = 640;
    const padPx = 28;

    const project = (lat: number, lng: number) => {
        const x = padPx + ((lng - (minLng - pad)) / lngSpan) * (width - padPx * 2);
        const y = padPx + ((maxLat + pad - lat) / latSpan) * (height - padPx * 2);

        return { x, y };
    };

    const projected = points.map((p) => ({ ...p, ...project(p.lat, p.lng) }));
    const polyline = projected.map((p) => `${p.x},${p.y}`).join(' ');

    return (
        <Box
            h={height}
            style={{
                borderRadius: 'var(--mantine-radius-md)',
                border: '1px solid var(--mantine-color-gray-3)',
                overflow: 'hidden',
                background: 'linear-gradient(160deg, #f4f7fb 0%, #e8eef6 100%)',
            }}
        >
            <svg
                width='100%'
                height={height}
                viewBox={`0 0 ${width} ${height}`}
                role='img'
                aria-label='Route preview'
            >
                {projected.length > 1 && (
                    <polyline
                        points={polyline}
                        fill='none'
                        stroke='var(--mantine-color-indigo-5)'
                        strokeWidth={3}
                        strokeLinejoin='round'
                        strokeLinecap='round'
                        opacity={0.85}
                    />
                )}
                {projected.map((p, index) => (
                    <g key={`${p.lat}-${p.lng}-${index}`}>
                        <circle
                            cx={p.x}
                            cy={p.y}
                            r={12}
                            fill='var(--mantine-color-indigo-6)'
                            stroke='#fff'
                            strokeWidth={2}
                        />
                        <text
                            x={p.x}
                            y={p.y + 1}
                            textAnchor='middle'
                            dominantBaseline='middle'
                            fill='#fff'
                            fontSize={11}
                            fontWeight={700}
                            fontFamily='inherit'
                        >
                            {index + 1}
                        </text>
                    </g>
                ))}
            </svg>
        </Box>
    );
};
